import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "../../../i18n/LocaleProvider";
import { Modal } from "../../settings/Modal";
import { Spinner } from "../../settings/Spinner";
import { CloseHostDialog, type CloseHostPrompt } from "../CloseHostDialog";
import type { CadInstance } from "../../../bimgo";
import type { PluginPatchResult, PluginsController } from "../../../hooks/usePlugins";
import { CATALOG, formatYearRange, pluginView, type PluginId } from "../../../lib/plugins";
import {
  canRemove,
  canWrite,
  defaultSelection,
  manageEmptyState,
  manageItems,
  needsClosedHost,
  selectable,
  type ManageAction,
  type ManageItem,
} from "./plan";

/** How a row's run went; `note` carries a live patch's reload outcome. */
type Result = { ok: true; note?: string } | { ok: false; reason: string };
/** Which of the two footer buttons started the run. */
type Op = "write" | "remove";

/** lucide `RotateCw`: detect again; it turns while detection runs. */
const REFRESH_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" />
  </svg>
);
/** lucide `Lock`: this year's host is open; the run will ask for it to be closed. */
const LOCK_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
/** A row's progress once running, in place of its checkbox: waiting, working (turns), done, failed. */
const MARKS = {
  queued: null,
  running: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.22-8.56" /></svg>
  ),
  ok: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
  ),
  err: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
  ),
};
/** The word on a row's pill; the versions sit beside it. */
const KIND_KEY: Record<ManageAction, string> = {
  install: "pluginManage.kind.install",
  update: "pluginManage.kind.update",
  downgrade: "pluginManage.kind.downgrade",
  repair: "pluginManage.kind.repair",
};
/** Provisioner failures that mean "the CAD host is open" — refused up front, or locked mid-copy. */
const HOST_OPEN_CODES = new Set(["revit-running", "autocad-running", "install-locked"]);
/** Choosing; running the ticked rows one by one; showing how each went. */
type Phase = "choose" | "running" | "done";

/**
 * Manage one product's plugin across every year the machine has, in one dialog: install,
 * update, repair, roll back and remove, many years at a time.
 *
 * Each row is one product year with its own status — what is on disk, which build, whether
 * its host is open or wants a restart — and the write it is waiting for. Every row something
 * can be done to starts ticked; the footer carries one button per operation, each counting
 * the ticked rows it applies to. A first install from the catalog is the same dialog with only
 * Install.
 *
 * The per-year handling of the old Manage dialog is kept: a whole-tree write or removal of a
 * year whose host is open asks for it to be closed first (#189) and Cancel writes nothing; a
 * command-set patch runs live and reports its reload; a running row narrates its phase. Rows
 * run one at a time and the list is frozen while they do, so each keeps its result after
 * detection refreshes behind it.
 */
export function PluginManageDialog({ open, onClose, vendor, plugins, instances }: {
  open: boolean;
  onClose: () => void;
  vendor: PluginId;
  plugins: PluginsController;
  instances: readonly CadInstance[];
}) {
  const { t } = useLocale();
  const host = CATALOG.find((entry) => entry.id === vendor)?.hostName ?? vendor;
  const snapshots = plugins.state.kind === "loaded" ? plugins.state.snapshots : null;
  const live = useMemo(
    () => manageItems(snapshots, instances, vendor, plugins.canPatch),
    [snapshots, instances, vendor, plugins.canPatch],
  );
  const view = useMemo(() => pluginView(snapshots, instances, vendor), [snapshots, instances, vendor]);
  const [phase, setPhase] = useState<Phase>("choose");
  const [op, setOp] = useState<Op>("write");
  const [frozen, setFrozen] = useState<ManageItem[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [results, setResults] = useState<Record<string, Result>>({});
  const [current, setCurrent] = useState<string | null>(null);
  // The main process's narration for the row in flight: downloading, installing, removing.
  const [stage, setStage] = useState<string | null>(null);
  // Only a removal asks again, in its own dialog naming every year it touches. A write runs
  // on Confirm: the rows already say what each year gets, and a host in the way is still
  // asked about before anything is written (`waitForHostsClosed`).
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  // "Close Revit 2025 first" (#189): the run waits on the answer.
  const [closePrompt, setClosePrompt] = useState<CloseHostPrompt | null>(null);
  const closeAnswer = useRef<((go: boolean) => void) | null>(null);
  // Set from the click until the run is under way or abandoned. `phase` only turns "running"
  // after the host check's await, so without this a double-click on Confirm (which no longer
  // opens a dialog in between) would start the same writes twice.
  const starting = useRef(false);
  // The same span as state, for the screen: the host check can take a few seconds, and
  // Confirm shows a spinner for it rather than looking frozen until installing starts.
  const [preparing, setPreparing] = useState<Op | null>(null);

  const items = phase === "choose" ? live : frozen;
  const liveByKey = useMemo(() => new Map(live.map((item) => [item.key, item])), [live]);
  const liveKey = live.map((item) => `${item.key}${selectable(item) ? "" : "!"}${item.action ?? ""}`).join(",");

  // A fresh detection starts from its own defaults: everything that can be acted on is ticked.
  useEffect(() => {
    if (phase === "choose") setSelected(defaultSelection(live));
    // `liveKey` stands for `live`: only a change in rows or their actions resets the ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveKey, phase]);


  // Every opening starts over with a fresh detection.
  useEffect(() => {
    if (!open) return;
    setPhase("choose");
    setResults({});
    plugins.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Phase narration: an older bridge has none, and a running row then says "Working…".
  useEffect(() => {
    const onProgress = window.bimgo?.plugins?.onProgress;
    if (!onProgress) return;
    return onProgress(({ phase: next }) => setStage(next));
  }, []);

  const refresh = () => {
    setPhase("choose");
    setResults({});
    plugins.reload();
  };

  const ticked = live.filter((item) => selected.includes(item.key));
  const toWrite = ticked.filter(canWrite);
  const toRemove = ticked.filter(canRemove);

  const reasonOf = (code: string, message: string, year: string) =>
    HOST_OPEN_CODES.has(code) ? t("plugins.update.hostOpen", { host, years: year }) : message || code;

  /** What a live patch did to the sessions it was applied under, worded as the old dialog did. */
  const reloadNote = (result: PluginPatchResult): string => {
    const reload = result.reload;
    if (reload == null) return t("plugins.update.reloadFailed", { host });
    if (reload.requested === 0) return t("plugins.update.reloadIdle");
    return reload.reloaded === reload.requested
      ? t("plugins.update.reloaded", { count: reload.reloaded })
      : t("plugins.update.reloadPartial", { reloaded: reload.reloaded, requested: reload.requested, host });
  };

  /**
   * Hold the run until these years' CAD host is closed (#189). Asked of a fresh detect, not of
   * the snapshot on screen; a session connected to the agent counts as open on the first look.
   * Resolves false when the user cancels, and nothing is written.
   */
  const waitForHostsClosed = async (chosen: ManageItem[]): Promise<boolean> => {
    if (!chosen.length) return true;
    const years = chosen.map((item) => item.year);
    const connected = chosen.filter((item) => item.hostOpen).map((item) => item.year);
    let openYears = [...new Set([...(await plugins.openYears(vendor, years)), ...connected])];
    let stillOpen = false;
    while (openYears.length > 0) {
      const go = await new Promise<boolean>((resolve) => {
        closeAnswer.current = resolve;
        setClosePrompt({ host, years: openYears, stillOpen, checking: false });
      });
      if (!go) {
        setClosePrompt(null);
        return false;
      }
      setClosePrompt((prev) => (prev ? { ...prev, checking: true } : prev));
      // The agent's session list lags a closed host by a heartbeat, so the re-check is the
      // process list's alone.
      openYears = await plugins.openYears(vendor, years);
      stillOpen = true;
    }
    setClosePrompt(null);
    return true;
  };
  const answerClose = (go: boolean) => {
    const resolve = closeAnswer.current;
    closeAnswer.current = null;
    resolve?.(go);
  };

  const run = async (which: Op) => {
    if (plugins.refreshing || plugins.state.kind !== "loaded" || phase !== "choose") return;
    const chosen = which === "write" ? toWrite : toRemove;
    if (!chosen.length || starting.current) return;
    starting.current = true;
    setPreparing(which);
    try {
      // Every whole-tree write and removal is checked against a fresh detect, not only the rows
      // already shown as open: a host opened since the last look is just as much in the way.
      if (!(await waitForHostsClosed(chosen.filter((item) => which === "remove" || !item.live)))) return;
      setOp(which);
      setFrozen(chosen);
      setResults({});
      setPhase("running");
    } finally {
      starting.current = false;
      setPreparing(null);
    }
    for (const item of chosen) {
      setCurrent(item.key);
      setStage(null);
      let result: Result;
      if (which === "remove") {
        const outcome = await plugins.uninstall(item.vendor, [item.year]);
        result = outcome.ok ? { ok: true } : { ok: false, reason: reasonOf(outcome.code, outcome.message, item.year) };
      } else if (item.live) {
        const outcome = await plugins.patch(item.vendor, [item.year]);
        result = outcome.ok ? { ok: true, note: reloadNote(outcome.result) } : { ok: false, reason: reasonOf(outcome.code, outcome.message, item.year) };
      } else {
        const outcome = await plugins.install(item.vendor, [item.year]);
        result = outcome.ok ? { ok: true } : { ok: false, reason: reasonOf(outcome.code, outcome.message, item.year) };
      }
      setResults((prev) => ({ ...prev, [item.key]: result }));
    }
    setCurrent(null);
    setStage(null);
    setPhase("done");
  };

  const loading = (plugins.refreshing || plugins.state.kind === "loading") && phase === "choose";
  const pickable = live.filter(selectable);
  const allTicked = pickable.length > 0 && pickable.every((item) => selected.includes(item.key));
  const someTicked = pickable.some((item) => selected.includes(item.key));
  const finished = Object.values(results);
  const succeeded = finished.filter((result) => result.ok).length;
  const pending = live.filter((item) => item.action !== null).length;
  const description = phase === "running"
    ? t(op === "remove" ? "pluginManage.progress.remove" : "pluginManage.progress.write", { done: finished.length, total: items.length })
    : phase === "done"
      ? finished.length > succeeded
        ? t("pluginManage.summary.partial", { done: succeeded, failed: finished.length - succeeded })
        : t("pluginManage.summary.done", { count: succeeded })
      : items.length && !loading
        ? pending
          ? t("pluginManage.found", { count: pending })
          : t(`pluginManage.empty.${manageEmptyState(snapshots, vendor)}`, { host })
        : undefined;

  // A detected year this desktop cannot serve, and which years do have a build when some row
  // has none (#52, #93).
  const unsupported = view?.unsupportedVersions ?? [];
  const supported = view?.supportedVersions ?? [];
  const unsupportedNote = unsupported.length > 0 && supported.length > 0
    ? t("plugins.install.unsupportedHost", { host, found: formatYearRange(unsupported), supported: formatYearRange(supported) })
    : null;
  const availableYears = view?.availableYears ?? [];
  const availableNote = availableYears.length > 0 && live.some((item) => item.unsupported)
    ? t("plugins.install.availableYears", { host, years: formatYearRange(availableYears) })
    : null;

  /** Left of a row: its checkbox while choosing, its progress once running. */
  const rowMark = (item: ManageItem) => {
    if (phase === "choose") {
      return (
        <input
          type="checkbox"
          checked={selected.includes(item.key) && selectable(item)}
          disabled={loading || preparing !== null || !selectable(item)}
          onChange={(event) => setSelected((list) => event.target.checked
            ? [...list, item.key]
            : list.filter((key) => key !== item.key))}
        />
      );
    }
    const result = results[item.key];
    const state = result ? (result.ok ? "ok" : "err") : item.key === current ? "running" : "queued";
    return <span className={`manage-mark is-${state}`} aria-hidden="true">{MARKS[state]}</span>;
  };
  /** The year's own status under its name: what is on disk, which build, host open, restart. */
  const statusLine = (item: ManageItem) => [
    item.unsupported ? t("plugins.state.unsupported") : t(`plugins.state.${item.state}`),
    item.installed,
    item.hostOpen ? t("plugins.state.openNow") : null,
    item.restartRequired ? t("plugins.state.restartRequired", { host }) : null,
  ].filter(Boolean).join(" · ");
  /** Under a row: how its run went. An open host is named in the status line; the warning to
      close it lives in the confirmation, where the user is about to act on it. */
  const note = (item: ManageItem) => {
    const result = results[item.key];
    if (result) {
      if (!result.ok) return <p className="manage-note is-err">{t("pluginManage.result.failed", { reason: result.reason })}</p>;
      return result.note ? <p className="manage-note">{result.note}</p> : null;
    }
    return null;
  };
  /** In the removal confirmation, under a year whose host has to be closed first. */
  const closeWarning = (item: ManageItem) =>
    needsClosedHost(item, "remove")
      ? <p className="manage-note is-warn">{LOCK_ICON}{t("pluginManage.closeFirst", { host: item.host, year: item.year })}</p>
      : null;
  /** Beside the year's name: the write it is waiting for, coloured by kind. Hidden once running. */
  const kindPill = (item: ManageItem) => {
    if (phase !== "choose" || !item.action) return null;
    return (
      <span
        className={`manage-kind is-${item.action}`}
        title={
          item.action === "repair"
            ? t("plugins.card.repairHint", { host })
            : item.action === "downgrade"
              ? t("plugins.card.downgradeHint", { host })
              : item.action === "install"
                ? t("plugins.card.installHint", { host })
                : item.live
                  ? t("plugins.card.updateHotHint")
                  : t("plugins.card.restartRequired", { host })
        }
      >
        {t(KIND_KEY[item.action])}
      </span>
    );
  };
  /** Right of a row: the version the write moves to, or its run state. */
  const trailing = (item: ManageItem) => {
    const result = results[item.key];
    if (result) return result.ok ? <span className="manage-state">{t("pluginManage.result.done")}</span> : null;
    if (phase === "running") {
      return (
        <span className="manage-state">
          {item.key !== current
            ? t("pluginManage.result.queued")
            : stage
              ? t(`plugins.progress.${stage}`)
              : t("plugins.progress.working")}
        </span>
      );
    }
    if (!item.action) return null;
    return (
      <>
        <span className="manage-version">
          {item.from ? (
            <>
              <span className="manage-from">{item.from}</span>
              <span className="manage-arrow" aria-hidden="true">→</span>
            </>
          ) : null}
          <span className="manage-to">{item.to}</span>
        </span>
      </>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closable={phase !== "running" && closePrompt === null && !confirmingRemove && preparing === null}
        title={t("pluginManage.title", { host })}
        description={description}
        icon={<span className={`plugin-glyph manage-glyph is-${vendor}`} aria-hidden="true">{vendor === "revit" ? "R" : "A"}</span>}
        actions={phase === "running" ? null : (
          <button
            type="button"
            className={`icon-btn manage-refresh${loading ? " is-spinning" : ""}`}
            onClick={refresh}
            disabled={loading || preparing !== null}
            title={loading ? t("pluginManage.detecting") : t("pluginManage.refresh")}
            aria-label={loading ? t("pluginManage.detecting") : t("pluginManage.refresh")}
          >
            {REFRESH_ICON}
          </button>
        )}
        footer={phase === "choose" ? (
          <>
            {toRemove.length ? (
              <button
                type="button"
                className="settings-btn secondary is-danger"
                onClick={() => setConfirmingRemove(true)}
                disabled={loading || preparing !== null}
              >
                {preparing === "remove" ? (
                  <span className="manage-preparing"><Spinner size={14} />{t("pluginManage.preparing")}</span>
                ) : t("pluginManage.run.remove", { count: toRemove.length })}
              </button>
            ) : null}
            <span className="manage-footer-gap" />
            {pending ? (
              <button type="button" className="settings-btn" onClick={() => void run("write")} disabled={!toWrite.length || loading || preparing !== null}>
                {/* One word for every write: each row already says whether it installs, updates,
                    repairs or rolls back. */}
                {preparing === "write" ? (
                  <span className="manage-preparing"><Spinner size={14} />{t("pluginManage.preparing")}</span>
                ) : t("pluginManage.run.confirm", { count: toWrite.length })}
              </button>
            ) : null}
          </>
        ) : (
          <button type="button" className="settings-btn" onClick={onClose} disabled={phase === "running"}>
            {phase === "running" ? t("pluginManage.result.running") : t("common.close")}
          </button>
        )}
      >
        {unsupportedNote ? <p className="settings-note">{unsupportedNote}</p> : null}
        {availableNote ? <p className="settings-note">{availableNote}</p> : null}
        {plugins.state.kind === "failed" && phase === "choose" ? (
          <p className="settings-msg err" role="alert">{plugins.state.message}</p>
        ) : loading && !items.length ? (
          <p className="manage-empty">{t("pluginManage.detecting")}</p>
        ) : !items.length ? (
          unsupportedNote ? null : <p className="manage-empty">{t("pluginManage.empty.no-hosts", { host })}</p>
        ) : (
          <div className={`manage-panel${loading ? " is-loading" : ""}`}>
            {phase === "choose" && pickable.length > 1 ? (
              <label className="manage-all">
                <input
                  type="checkbox"
                  checked={allTicked}
                  disabled={loading || preparing !== null}
                  ref={(el) => { if (el) el.indeterminate = someTicked && !allTicked; }}
                  onChange={() => setSelected(allTicked ? [] : pickable.map((item) => item.key))}
                />
                <span>{t("pluginManage.selectAll")}</span>
                <span className="manage-all-count">
                  {t("pluginManage.selected", { count: pickable.filter((item) => selected.includes(item.key)).length, total: pickable.length })}
                </span>
              </label>
            ) : null}
            <ul className="manage-list">
              {items.map((item) => (
                <li
                  key={item.key}
                  className={`manage-row${!selectable(item) && phase === "choose" ? " is-blocked" : ""}${item.key === current ? " is-current" : ""}`}
                >
                  <label>
                    {rowMark(item)}
                    <span className="manage-title">
                      <span className="manage-name">
                        {item.host} {item.year}
                        {kindPill(item)}
                      </span>
                      {/* The latest detection's status, even while the rows are frozen: a row that was
                          just removed says "Not installed", not what it was before the run. */}
                      <span className="manage-status">{statusLine(liveByKey.get(item.key) ?? item)}</span>
                    </span>
                    {trailing(item)}
                  </label>
                  {note(item)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {phase === "done" && op === "write" && succeeded > 0 && frozen.some((item) => !item.live) ? (
          <p className="manage-restart">{t("pluginManage.restart", { host })}</p>
        ) : null}
      </Modal>

      {/* After the Manage dialog, so it stacks above it. */}
      <Modal
        open={confirmingRemove}
        onClose={() => setConfirmingRemove(false)}
        title={t("pluginManage.removeConfirm.title", { host })}
        footer={
          <>
            <button type="button" className="settings-btn secondary" onClick={() => setConfirmingRemove(false)}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="settings-btn is-danger"
              onClick={() => {
                setConfirmingRemove(false);
                void run("remove");
              }}
            >
              {t("pluginManage.run.remove", { count: toRemove.length })}
            </button>
          </>
        }
      >
        <p className="settings-note">{t("pluginManage.removeConfirm.body", { host })}</p>
        <ul className="manage-confirm-list">
          {toRemove.map((item) => (
            <li key={item.key}>
              <div className="manage-confirm-row">
                <span className="manage-name">{item.host} {item.year}</span>
                <span className="manage-status">
                  {[item.installed, item.state === "external" ? t("plugins.state.external") : null]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
              {closeWarning(item)}
            </li>
          ))}
        </ul>
      </Modal>
      <CloseHostDialog prompt={closePrompt} onContinue={() => answerClose(true)} onCancel={() => answerClose(false)} />
    </>
  );
}
